// Signaling WSS client (subprotocol ls-signal-v3).
//
// Auth is payload-based (common/02 §6.10 / ADR-040 two-listener): the FIRST frame
// after the socket opens is signin{access_token}; the connection counts as "open"
// only after signin_ok. No token in the URL (it would leak into edge logs /
// browser history — signaling-recording#1) and no Authorization header (browsers
// can't set one; the server reads neither). get_self is the first REQUEST after
// signin_ok (00 §8.2's "first frame" predates the signin handshake).
//
// Reconnect backoff per specs/10_client_offline_sync.md §4.3 (the "WSS 重連 backoff
// (單一定義)" at the end of §4.3, the single app-wide definition): 1s → 2s → 5s →
// 10s → 30s, then fixed 30s, each ±20% jitter. Before each reconnect the token is
// CHECKED (§4.3: "now > exp-60s 才先 refresh") and only refreshed when stale, so a
// still-fresh token reconnects without a needless /v3/auth/refresh round-trip.
//
// session-expired vs transient (§4.3 six-state machine): session-expired means the
// refresh token is GONE (refresh → 400/401 / 30d expiry). A network error or REST 5xx
// is a TRANSIENT failure — it must stay in degraded/offline and keep backing off, NOT
// log the user out. So a failed pre-reconnect/token_expiring refresh emits
// session_expired ONLY for an ApiError 400/401; everything else schedules another
// backoff attempt (which will retry the refresh next round).
//
// Session signals: token_expiring → REST refresh then auth_refresh on the SAME
// socket (no reconnect); force_logout → classify by reason and route (see below).
//
// force_logout routing (common/02 §6.9 + APP specs 18 §3.3 / 20 §3.2): the server
// sends {type:'force_logout', reason, scope, timestamp} for ANY revocation. Treating
// EVERY force_logout as a full account logout is the bug this module guards against —
// revoking ONE shared device (reason:'authz_revoked') must NOT log the whole account out.
//
// SCOPE-FIRST (common#53): `scope` is the server's own statement of the blast radius
// and wins whenever it is present and recognised. Only when it is absent (old server /
// old path) or unrecognised do we fall back to the reason table below — which is why a
// reason this build has never seen no longer means "log this user out". The fallback is
// still fail-safe-to-account, so nothing gets quietly less conservative.
//   authz_revoked / device_disabled / device_unbound → device-scoped: drop that
//     device only, keep the auth session (specs 18 §3.3 table / §3.4; device_unbound
//     is the device-initiated unbind pushed to owner+shares, spec 23 §5.7/§8).
//     device_id MAY ride the frame (top-level or in payload) — the rev:authz pub/sub
//     upstream carries {device_id,user_id} (common/05) — but common/02 §6.9's canonical
//     example shows only {reason,timestamp}, so the id is best-effort; the device-list
//     reload is the authoritative cleanup (spec 18 §3.4: next GET /v3/devices drops it).
//   password_changed → own rev:user from change-password (spec 20 §3.2): the kept
//     session must attempt a SILENT refresh + reconnect, NOT a logout UI; only a
//     failed refresh falls through to session end.
//   token_expired → same session-refresh handling, and NOT a revocation at all: the
//     server binds each client WSS to its JWT exp and kicks the connection at expiry
//     (common/02 §6.8; signaling-recording websocket.go #15). The refresh token is
//     still valid, so a full logout here would throw EVERY session longer than
//     RA_ACCESS_TOKEN_TTL (1h) back to the login screen.
//   account_suspended / account_deleted / logout (+ unknown) → account-scoped:
//     real logout (the existing behaviour).

import { config } from '../config';
import { ApiError, refreshAccessToken, tokenStore } from './api';

export const CMD_TIMEOUT_MS = 30_000;
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** force_logout.reason enum (common/02 §6.9). Unknown values fall back to account scope. */
export type ForceLogoutReason =
  | 'logout'
  | 'account_suspended'
  | 'account_deleted'
  | 'password_changed'
  | 'authz_revoked'
  | 'device_disabled'
  | 'device_unbound'
  | 'token_expired';

export type RevocationScope =
  /** Drop one device, keep the session (authz_revoked / device_disabled / device_unbound). */
  | 'device'
  /** Silent refresh + reconnect, no logout UI (password_changed / token_expired). */
  | 'session-refresh'
  /** Whole account is gone — end the session (account_suspended / account_deleted / logout / unknown). */
  | 'account';

export interface RevocationInfo {
  scope: RevocationScope;
  /** Raw reason string off the wire (may be undefined / unrecognised). */
  reason?: string;
  /** Affected device for scope:'device' when the server forwarded it (best-effort). */
  deviceId?: string;
  /** The original frame, for callers that need timestamp / extra fields. */
  frame: SignalFrame;
}

/** Pull device_id off a force_logout frame: top-level wins, else payload.device_id. */
function frameDeviceId(frame: SignalFrame): string | undefined {
  if (typeof frame.device_id === 'string' && frame.device_id) return frame.device_id;
  const pd = (frame.payload as { device_id?: unknown } | undefined)?.device_id;
  return typeof pd === 'string' && pd ? pd : undefined;
}

/**
 * Server-declared scope (common/02 §6.9 rule 1) — authoritative when present AND
 * recognised. An unrecognised value is treated as absent (rule 3): a scope we cannot
 * interpret must never widen or narrow the blast radius by accident.
 */
function frameScope(frame: SignalFrame): RevocationScope | undefined {
  const s = typeof frame.scope === 'string' ? frame.scope : undefined;
  switch (s) {
    case 'device':
    case 'session-refresh':
    case 'account':
      return s;
    default:
      return undefined;
  }
}

/**
 * Reasons common/02 §6.9 declares account-scope. They ride the `default` arm of
 * {@link reasonScope}, but they are ALSO listed here because rule 1a needs to tell
 * "known account revocation" apart from "reason I have never heard of" — the two are
 * indistinguishable once both have fallen through to the default.
 */
const ACCOUNT_REASONS: readonly string[] = ['logout', 'account_suspended', 'account_deleted'];

/**
 * Fallback classification for frames from a server that does not send `scope`
 * (common/02 §6.9 rule 2). Kept in sync with the §6.9 table by the CI parity guard.
 */
function reasonScope(reason: string | undefined): RevocationScope {
  switch (reason) {
    case 'authz_revoked':
    case 'device_disabled':
    case 'device_unbound':
      return 'device';
    // token_expired is NOT a revocation: the server binds each client WSS to its JWT exp
    // and kicks the connection at expiry (common/02 §6.8; signaling-recording websocket.go,
    // #15). The refresh token is still valid — a full logout here would throw every session
    // longer than RA_ACCESS_TOKEN_TTL (1h) back to the login screen.
    case 'password_changed':
    case 'token_expired':
      return 'session-refresh';
    // account_suspended / account_deleted / logout / undefined / anything unknown:
    // fail safe to a full logout rather than silently keeping a revoked session.
    default:
      return 'account';
  }
}

/**
 * Classify a force_logout frame into a {@link RevocationInfo}. Pure + exported so the
 * scope contract is unit-testable without a live socket.
 *
 * Scope-first (common/02 §6.9, common#53): the server states the blast radius, so a
 * reason this build has never heard of no longer degrades to a full account logout —
 * which is what made every new reason a silent breaking change for shipped clients.
 * `reason` survives as UI copy / telemetry and as the fallback key for servers or
 * paths that do not send `scope` yet.
 */
export function classifyForceLogout(frame: SignalFrame): RevocationInfo {
  const reason = typeof frame.reason === 'string' ? frame.reason : undefined;
  const declared = frameScope(frame);
  // §6.9 rule 1a: a server-declared scope may not SOFTEN a reason this build already
  // knows to be an account revocation. Scope-first exists for reasons we do not
  // recognise; for the ones we do, a server that pairs `account_deleted` with
  // scope:'device' is buggy or hostile, and honouring it would leave a revoked
  // session alive — a fail-open the pre-scope code could not produce. Unknown
  // reasons still follow scope, so the feature keeps all of its value.
  const scope =
    declared !== undefined && !ACCOUNT_REASONS.includes(reason ?? '')
      ? declared
      : reasonScope(reason);
  // device scope with no device_id: keep the session and let the device-list reload
  // converge — never escalate to account (that is the bug this whole contract exists
  // to prevent). deviceId stays undefined and callers fall back to a plain reload.
  return scope === 'device'
    ? { scope, reason, deviceId: frameDeviceId(frame), frame }
    : { scope, reason, frame };
}

function jitter(ms: number): number {
  return Math.round(ms * (0.8 + Math.random() * 0.4)); // ±20%
}

/**
 * Does a refreshAccessToken() rejection mean the SESSION is gone (→ session_expired),
 * or just a transient failure (→ keep backing off)? Per spec 10 §4.3 only a refresh
 * that the server actively rejected (ApiError 400/401 — refresh cookie invalid/expired)
 * is session-expired. Network errors, REST 5xx, timeouts → transient.
 */
export function isSessionExpiredError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 400 || err.status === 401);
}

function uuid(): string {
  return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : 'req-' + Math.random().toString(16).slice(2);
}

// ---- frame types ----

export interface SignalFrame {
  type?: string;
  command?: string;
  request_id?: string;
  device_id?: string;
  /** ADR-045 NVR/DVR 1-based channel envelope. */
  channel?: number;
  session_id?: string;
  payload?: Record<string, unknown> & { [k: string]: unknown };
  [k: string]: unknown;
}

export type SignalingState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SignalingEvents {
  /** Connection state changes (drive the spec-10 §5 connection state machine UI). */
  state: (state: SignalingState) => void;
  /** Every parsed inbound frame (after correlation/session handling). */
  message: (frame: SignalFrame) => void;
  /** type:'event' frames (device events pushed on subscribed rooms). */
  event: (frame: SignalFrame) => void;
  /** Remote ICE candidate frames (top-level or command ice_candidate). */
  ice_candidate: (frame: SignalFrame) => void;
  /** get_self response payload (sent automatically on every open). */
  self: (payload: Record<string, unknown>) => void;
  /**
   * ACCOUNT-scoped force_logout (account_suspended / account_deleted / logout /
   * unknown reason) — auth layer must END the session. Device-scoped revocations do
   * NOT fire this; they fire `device_revoked`. See {@link classifyForceLogout}.
   */
  force_logout: (info: RevocationInfo) => void;
  /**
   * DEVICE-scoped revocation (authz_revoked / device_disabled / device_unbound): drop
   * the affected device, keep the session. `deviceId` is best-effort (specs 18 §3.3/§3.4;
   * device_unbound = device-initiated unbind, spec 23 §5.7/§8).
   */
  device_revoked: (info: RevocationInfo) => void;
  /**
   * Own change-password rev:user (password_changed): attempt a SILENT refresh +
   * reconnect, no logout UI (spec 20 §3.2). Auth layer decides keep-vs-end.
   */
  session_revoked: (info: RevocationInfo) => void;
  /** Token refresh failed during token_expiring / reconnect — treat as session expired. */
  session_expired: () => void;
}

type EventName = keyof SignalingEvents;

interface Pending {
  resolve: (frame: SignalFrame) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class SignalingTimeoutError extends Error {
  readonly requestId: string;
  constructor(requestId: string) {
    super('signaling command timeout (30s)');
    this.name = 'SignalingTimeoutError';
    this.requestId = requestId;
  }
}

/** Per-connection room bookkeeping for idempotent subscribe (see subscribe()). */
interface RoomEntry {
  /** How many live callers currently want this room (live page / events feed / settings). */
  refs: number;
  /** The single in-flight-or-settled subscribe request for this connection epoch. */
  promise: Promise<SignalFrame>;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private state: SignalingState = 'idle';
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  private readonly pending = new Map<string, Pending>();
  // Rooms subscribed on the CURRENT socket. Server room membership dies with the
  // connection, so this map is cleared on every close; callers re-subscribe on
  // 'open' (existing contract, see subscribe()).
  private readonly rooms = new Map<string, RoomEntry>();
  private readonly listeners: { [K in EventName]: Set<SignalingEvents[K]> } = {
    state: new Set(),
    message: new Set(),
    event: new Set(),
    ice_candidate: new Set(),
    self: new Set(),
    force_logout: new Set(),
    device_revoked: new Set(),
    session_revoked: new Set(),
    session_expired: new Set(),
  };

  getState(): SignalingState {
    return this.state;
  }

  on<K extends EventName>(event: K, fn: SignalingEvents[K]): () => void {
    this.listeners[event].add(fn);
    return () => this.listeners[event].delete(fn);
  }

  off<K extends EventName>(event: K, fn: SignalingEvents[K]): void {
    this.listeners[event].delete(fn);
  }

  private emit<K extends EventName>(event: K, ...args: Parameters<SignalingEvents[K]>): void {
    for (const fn of this.listeners[event]) {
      (fn as (...a: Parameters<SignalingEvents[K]>) => void)(...args);
    }
  }

  private setState(s: SignalingState): void {
    if (this.state === s) return;
    this.state = s;
    this.emit('state', s);
  }

  /** Open (or no-op if already open/connecting). Requires a token in tokenStore. */
  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.closedByUser = false;
    this.open();
  }

  /** Close for good (no reconnect). Rejects all pending requests. */
  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.rooms.clear();
    this.failAllPending(new Error('signaling closed'));
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* already closed */
      }
      this.ws = null;
    }
    this.setState('closed');
  }

  private open(): void {
    // A reconnect timer that already fired can land here AFTER close() (logout /
    // account-revoke) set closedByUser — opening would resurrect an authenticated
    // socket and re-send signin{token} for a logged-out session. Refuse.
    if (this.closedByUser) return;
    const token = tokenStore.get();
    if (!token) {
      this.emit('session_expired');
      return;
    }
    this.setState(this.attempts > 0 ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(config.signalingUrl, 'ls-signal-v3');
    this.ws = ws;

    ws.onopen = () => {
      // First frame MUST be signin{access_token} (common/02 §6.10); the state
      // stays connecting until signin_ok arrives (handled in handleFrame).
      ws.send(
        JSON.stringify({ type: 'signin', access_token: token, timestamp: Math.floor(Date.now() / 1000) })
      );
    };
    ws.onmessage = (ev) => {
      let frame: SignalFrame;
      try {
        frame = JSON.parse(ev.data as string) as SignalFrame;
      } catch {
        return;
      }
      this.handleFrame(frame);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      // Server-side room membership is per-connection — forget it so the callers'
      // re-subscribe on the next 'open' actually hits the wire again.
      this.rooms.clear();
      this.failAllPending(new Error('signaling disconnected'));
      if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows and drives the reconnect */
    };
  }

  private scheduleReconnect(): void {
    const delay = jitter(BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)]);
    this.attempts += 1;
    this.setState('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Spec 10 §4.3: CHECK the token before reconnecting; refresh only when stale
      // (now > exp-60s). A still-fresh token reconnects straight away — no needless
      // /v3/auth/refresh (which also avoids an extra chance of being mis-killed).
      if (tokenStore.isFresh()) {
        this.open();
        return;
      }
      refreshAccessToken().then(
        () => this.open(),
        (err: unknown) => {
          // Refresh token actively rejected (400/401) → session is gone. Any other
          // failure (network / 5xx) is transient: schedule another backoff round and
          // retry the refresh next time instead of logging the user out (§4.3).
          if (isSessionExpiredError(err)) this.emit('session_expired');
          else this.scheduleReconnect();
        }
      );
    }, delay);
  }

  private handleFrame(frame: SignalFrame): void {
    // signin handshake (common/02 §6.10): the connection is usable only after
    // signin_ok. attempts resets HERE (not on socket open) so a token rejected
    // in signin_error keeps the §5 reconnect backoff honest.
    if (frame.type === 'signin_ok') {
      this.attempts = 0;
      this.setState('open');
      // get_self is the first request after signin_ok (00 §8.2); subscriptions follow.
      void this.request('get_self').then(
        (m) => this.emit('self', (m.payload as Record<string, unknown>) ?? {}),
        () => {
          /* surfaced via timeout; non-fatal */
        }
      );
      return;
    }
    if (frame.type === 'signin_error') {
      // Token rejected at sign-in. Close and let the reconnect path refresh the
      // token first (spec 10 §5); a failed refresh surfaces session_expired there.
      this.ws?.close();
      return;
    }
    // Server-initiated session signals (00 §8.3 / signaling §6.8–6.9).
    if (frame.type === 'token_expiring') {
      refreshAccessToken().then(
        (token) => this.sendRaw({ type: 'command', request_id: uuid(), command: 'auth_refresh', payload: { access_token: token } }),
        (err: unknown) => {
          // Only a 400/401 (refresh token gone) is session-expired; a transient
          // failure leaves the still-valid current token in place and lets the next
          // token_expiring / reconnect retry the refresh (§4.3). Do NOT log out.
          if (isSessionExpiredError(err)) this.emit('session_expired');
        }
      );
      return;
    }
    if (frame.type === 'force_logout') {
      // Route by reason — NOT every force_logout ends the account session (the bug).
      const info = classifyForceLogout(frame);
      if (info.scope === 'device') {
        // The server has already dropped this room's subscription on its side
        // (rev:authz / unbind) and — since sig-rec!122 — no longer closes the socket,
        // which is what used to clear `rooms` for us. Evict our entry explicitly:
        // otherwise a re-granted share on this same socket would hit the idempotent
        // cache in subscribe(), send no wire frame, and sit in a room the server does
        // not have us in — silently no events until the next reconnect.
        if (info.deviceId) this.rooms.delete(info.deviceId);
        this.emit('device_revoked', info);
      }
      else if (info.scope === 'session-refresh') this.emit('session_revoked', info);
      else this.emit('force_logout', info);
      return;
    }

    // request_id correlation (report/response/error settle a pending request).
    const rid = frame.request_id;
    if (rid && this.pending.has(rid) && (frame.type === 'report' || frame.type === 'response' || frame.type === 'error')) {
      const p = this.pending.get(rid)!;
      this.pending.delete(rid);
      clearTimeout(p.timer);
      if (frame.type === 'error') {
        // ⚠️ 伺服端 ErrorMsg 的 message 在**頂層**,不在 payload 裡(見
        // signaling-recording protocol/message.go:ErrorMsg —— 無 payload 欄位)。
        // 原本只讀 payload.message,讀不到就退 code,於是「device viewer limit
        // reached」在 client 端變成 "forbidden",所有訊息特徵分類都是死碼。
        // 用 || 而非 ?? :空字串也要往下退。
        const err = new Error(String(
          (frame as { message?: string }).message
          || (frame.payload as { message?: string })?.message
          || frame['code']
          || 'signaling error'));
        (err as Error & { frame: SignalFrame }).frame = frame;
        p.reject(err);
      } else {
        p.resolve(frame);
      }
      this.emit('message', frame);
      return;
    }

    // Remote ICE arrives as a top-level frame OR command-shaped (legacy handles both).
    if (frame.type === 'ice_candidate' || frame.command === 'ice_candidate') {
      this.emit('ice_candidate', frame);
      this.emit('message', frame);
      return;
    }

    // Surfacing to 'event' listeners (room broadcasts):
    //   - type:'event'   → device events (TypeEvent, MoveStop, …).
    //   - type:'report' with NO matching pending request → an UNSOLICITED
    //     attribute-change report (common/04 §5.11 / signaling 02 §6.11, ADR-046):
    //     on op:set success WHERE THE VALUE CHANGED the device proactively re-emits
    //     op:report for the same attr id, request_id-less, broadcast to the room.
    //     Solicited reports (a matching request_id) were already settled + returned
    //     above; only the request_id-less ones reach here, so listeners see just the
    //     unsolicited change feed.
    if (frame.type === 'event' || frame.type === 'report') this.emit('event', frame);
    this.emit('message', frame);
  }

  /** Send any frame as-is (e.g. top-level {type:'ice_candidate', session_id, device_id, candidate}). */
  sendRaw(frame: SignalFrame): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(frame));
    return true;
  }

  /**
   * Send a {type:'command'} frame and await the correlated response/report.
   * Rejects with SignalingTimeoutError after 30s of silence so UIs never hang.
   * Extra top-level fields (e.g. device_id) go in `extra`.
   */
  request(command: string, payload?: Record<string, unknown>, extra?: Record<string, unknown>): Promise<SignalFrame> {
    return this.sendCorrelated({ type: 'command', command, ...(payload !== undefined ? { payload } : {}), ...extra });
  }

  /**
   * common/04 op model: {type:'cmd', request_id, device_id, op, id, ...extra}.
   * NOTE (ADR-046): recording mode/location/quality — LS 自家 app 走 ws op:set
   * (enumVal:int, 0x8050 business ids); restful PUT /v3/devices/{id}/settings/recording
   * 保留給 3rd-party。
   */
  sendCmd(deviceId: string, op: 'set' | 'get', id: number | string, extra?: Record<string, unknown>): Promise<SignalFrame> {
    return this.sendCorrelated({ type: 'cmd', device_id: deviceId, op, id, ...extra });
  }

  private sendCorrelated(frame: SignalFrame): Promise<SignalFrame> {
    const request_id = uuid();
    return new Promise<SignalFrame>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.has(request_id)) {
          this.pending.delete(request_id);
          reject(new SignalingTimeoutError(request_id));
        }
      }, CMD_TIMEOUT_MS);
      this.pending.set(request_id, { resolve, reject, timer });
      if (!this.sendRaw({ ...frame, request_id })) {
        this.pending.delete(request_id);
        clearTimeout(timer);
        reject(new Error('signaling not connected'));
      }
    });
  }

  /**
   * Subscribe to a device room (after open; re-subscribe on every 'open' is the
   * caller's job — the per-connection room map is cleared on close).
   *
   * IDEMPOTENT + REF-COUNTED: multiple callers (live page mount + its
   * 'open' listener, events feed, settings page) may want the same room
   * concurrently. Only the FIRST call per connection sends a wire frame; later
   * calls share the same request promise and bump a refcount so that one
   * caller's unsubscribe() cannot tear the room from under another.
   */
  subscribe(deviceId: string): Promise<SignalFrame> {
    const existing = this.rooms.get(deviceId);
    if (existing) {
      existing.refs += 1;
      return existing.promise;
    }
    const entry: RoomEntry = { refs: 1, promise: Promise.resolve({} as SignalFrame) };
    const evict = () => {
      // Only evict OUR entry — close() may have cleared the map and a newer
      // connection epoch may already own this key.
      if (this.rooms.get(deviceId) === entry) this.rooms.delete(deviceId);
    };
    entry.promise = this.request('subscribe', undefined, { device_id: deviceId }).then(
      (frame) => {
        // type:'response' with non-ok status (e.g. forbidden) = not subscribed.
        const status = typeof frame.status === 'string' ? frame.status : 'ok';
        if (status !== 'ok') {
          evict();
          const err = new Error(`subscribe ${deviceId} failed: ${status}`);
          (err as Error & { frame: SignalFrame }).frame = frame;
          throw err;
        }
        return frame;
      },
      (err: unknown) => {
        // Failed (error frame / timeout / disconnect): allow a retry to re-send.
        evict();
        throw err;
      }
    );
    this.rooms.set(deviceId, entry);
    return entry.promise;
  }

  /**
   * Drop one caller's interest in a device room; the wire unsubscribe is sent
   * only when the LAST caller leaves (refcount reaches zero).
   */
  unsubscribe(deviceId: string): Promise<SignalFrame> {
    const entry = this.rooms.get(deviceId);
    if (!entry) {
      // Room not tracked on this socket (e.g. the map was cleared by a reconnect or
      // close). There is nothing to tear down — sending a wire unsubscribe here would
      // be an unmatched frame, and would tear a room out from under another epoch's
      // subscriber. No-op.
      return Promise.resolve({} as SignalFrame);
    }
    entry.refs -= 1;
    if (entry.refs > 0) return entry.promise;
    this.rooms.delete(deviceId);
    return this.request('unsubscribe', undefined, { device_id: deviceId });
  }

  private failAllPending(err: Error): void {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pending.clear();
  }
}

/** App-wide singleton; feature code may also construct private instances for tests. */
export const signaling = new SignalingClient();
